import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  BankAccount, BankAccountRequest, BankTransaction, BankingSummary, TransactionCategory,
  AccountPurpose, BankImportResult, MatchSuggestion, InvoiceMatchCandidate, CategoryKind,
  VatSplitRequest, TransactionCategoryRequest,
} from '../types';
import bankingService, { TransactionFilter } from '../services/bankingService';
import businessAreaService from '../services/businessAreaService';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Kontoumsätze: Auszüge einlesen, Umsätze kategorisieren, Zahlungseingänge den
 * Rechnungen zuordnen.
 *
 * Der Import ordnet nur dann selbst zu, wenn Rechnungsnummer **und** Betrag
 * stimmen. Alles andere landet als Vorschlag hier und wird von Hand bestätigt.
 */

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('de-DE') : '—';

const centsToInput = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');
const inputToCents = (v: string): number => {
  const n = parseFloat(v.replace(/[^0-9,.-]/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : Math.round(n * 100);
};

const PURPOSE_LABEL: Record<AccountPurpose, string> = {
  OPERATING: 'Geschäftskonto',
  TAX_RESERVE: 'Steuerrücklage',
  SAVINGS: 'Rücklage',
};

const KIND_LABEL: Record<CategoryKind, string> = {
  INCOME: 'Ertrag',
  EXPENSE: 'Aufwand',
  TRANSFER: 'Umbuchung',
  TAX: 'Steuern',
  PRIVATE: 'Privat',
};

/**
 * Steuersätze zur Auswahl. 19 % ist der Regelsatz, 7 % gilt u.a. für Bücher und
 * Fahrscheine, 0 % für steuerfreie Umsätze (Versicherungen, Bankgebühren) und für
 * Reverse-Charge-Rechnungen aus dem Ausland — dort steht keine deutsche Vorsteuer drauf.
 */
const VAT_RATES = [19, 7, 0];

/** "Q3/2026" zu einem ISO-Datum, oder null wenn keins da ist. */
const quarterOf = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return `Q${Math.floor(d.getMonth() / 3) + 1}/${d.getFullYear()}`;
};

/** IBAN in Vierergruppen, wie sie auf Kontoauszügen steht. */
const formatIban = (iban: string): string => iban.replace(/(.{4})/g, '$1 ').trim();

const emptyAccount = (): BankAccountRequest => ({
  name: '',
  iban: '',
  bic: '',
  bankName: '',
  purpose: 'OPERATING',
  currency: 'EUR',
  openingBalanceCents: 0,
  openingBalanceDate: null,
  notes: '',
  active: true,
});

const Banking: React.FC = () => {
  usePageTitle('Konten');

  const [summary, setSummary] = useState<BankingSummary | null>(null);
  const [categories, setCategories] = useState<TransactionCategory[]>([]);
  // Die schon vergebenen Vorhaben — Vorschläge für die Spalte, damit aus „Radsport-Events" und
  // „Radsport Events" nicht zwei werden.
  const [businessAreas, setBusinessAreas] = useState<string[]>([]);
  // Kategorie-Pflege: `categoryForm` offen heißt Dialog offen, `editingCategoryId` null = neu.
  const [categoryForm, setCategoryForm] = useState<TransactionCategoryRequest | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [showCategoryAdmin, setShowCategoryAdmin] = useState(false);
  // Abgeschaltete Kategorien stehen nicht im Weg, bis jemand sie sucht.
  const [showInactiveCategories, setShowInactiveCategories] = useState(false);
  const [allCategories, setAllCategories] = useState<TransactionCategory[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [candidates, setCandidates] = useState<InvoiceMatchCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [filter, setFilter] = useState<TransactionFilter>({ limit: 300 });
  const [search, setSearch] = useState('');

  const [accountForm, setAccountForm] = useState<BankAccountRequest | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [importAccountId, setImportAccountId] = useState<number | null>(null);
  const [lastImport, setLastImport] = useState<BankImportResult | null>(null);
  const [matchingFor, setMatchingFor] = useState<BankTransaction | null>(null);
  const [splittingFor, setSplittingFor] = useState<BankTransaction | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const [imports, setImports] = useState<BankImportResult[]>([]);
  const [showImports, setShowImports] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  const accounts = summary?.accounts ?? [];

  const loadBase = useCallback(async () => {
    const [s, c, i, areas] = await Promise.all([
      bankingService.getSummary(),
      bankingService.getCategories(),
      bankingService.getImports(undefined, 20),
      // Ohne Vorschläge bleibt die Spalte ein normales Textfeld — kein Grund, die Seite
      // deswegen scheitern zu lassen.
      businessAreaService.list().catch(() => [] as string[]),
    ]);
    setSummary(s);
    setCategories(c);
    setImports(i);
    setBusinessAreas(areas);
  }, []);

  const loadTransactions = useCallback(async (f: TransactionFilter) => {
    const t = await bankingService.getTransactions(f);
    setTransactions(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadBase(), loadTransactions({ limit: 300 })]);
      } catch {
        toast.error('Die Kontodaten ließen sich nicht laden.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBase, loadTransactions]);

  // Filter wirken direkt; die Suche wartet kurz, damit nicht jeder Tastendruck lädt.
  useEffect(() => {
    const handle = setTimeout(() => {
      loadTransactions({ ...filter, search: search.trim() || undefined })
        .catch(() => toast.error('Die Umsätze ließen sich nicht laden.'));
    }, search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [filter, search, loadTransactions]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadBase(),
      loadTransactions({ ...filter, search: search.trim() || undefined }),
    ]);
  }, [loadBase, loadTransactions, filter, search]);

  // ============================================================
  // Konten
  // ============================================================

  const saveAccount = async () => {
    if (!accountForm) return;
    if (!accountForm.name.trim() || !accountForm.iban.trim()) {
      toast.error('Name und IBAN sind nötig.');
      return;
    }
    setBusy(true);
    try {
      if (editingAccountId) {
        await bankingService.updateAccount(editingAccountId, accountForm);
        toast.success('Konto gespeichert.');
      } else {
        await bankingService.createAccount(accountForm);
        toast.success('Konto angelegt.');
      }
      setAccountForm(null);
      setEditingAccountId(null);
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Das Konto ließ sich nicht speichern.');
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  // Import
  // ============================================================

  const startImport = (accountId: number) => {
    setImportAccountId(accountId);
    fileInput.current?.click();
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || importAccountId == null) return;

    setBusy(true);
    try {
      const result = await bankingService.importStatement(importAccountId, file);
      setLastImport(result);
      if (result.rowsImported === 0) {
        toast('Alle Buchungen aus der Datei lagen schon vor.', { icon: 'ℹ️' });
      } else {
        toast.success(
          `${result.rowsImported} neue Buchung${result.rowsImported === 1 ? '' : 'en'}` +
          (result.invoicesMatched > 0
            ? `, ${result.invoicesMatched} Rechnung${result.invoicesMatched === 1 ? '' : 'en'} auf bezahlt gesetzt.`
            : '.'),
        );
      }
      if (result.suggestions.length > 0) {
        setCandidates(await bankingService.getInvoiceCandidates());
      }
      await refreshAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? 'Die Datei ließ sich nicht einlesen.');
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  // Umsatz bearbeiten
  // ============================================================

  const setCategory = async (tx: BankTransaction, categoryId: number | null) => {
    try {
      await bankingService.updateTransaction(tx.id, {
        categoryId: categoryId ?? undefined,
        clearCategory: categoryId === null,
      });
      await refreshAll();
    } catch {
      toast.error('Die Kategorie ließ sich nicht setzen.');
    }
  };

  const revertImport = async (imp: BankImportResult) => {
    if (imp.id == null) return;
    const ok = window.confirm(
      `Import „${imp.fileName}" zurücknehmen?\n\n` +
      `${imp.rowsImported} Umsätze werden gelöscht, samt ihrer Kategorien und Zuordnungen. ` +
      `Rechnungen, die dieser Import auf „bezahlt" gesetzt hat, bleiben bezahlt.`,
    );
    if (!ok) return;

    setBusy(true);
    try {
      const result = await bankingService.revertImport(imp.id);
      const nachsatz = result.affectedInvoiceNumbers.length > 0
        ? ` Ohne Zahlungsbeleg stehen jetzt: ${result.affectedInvoiceNumbers.join(', ')}.`
        : '';
      toast.success(`${result.deletedCount} Umsätze gelöscht.${nachsatz}`, { duration: 8000 });
      setLastImport(prev => (prev?.id === imp.id ? null : prev));
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Der Import ließ sich nicht zurücknehmen.');
    } finally {
      setBusy(false);
    }
  };

  const setDocumentDate = async (tx: BankTransaction, date: string | null) => {
    try {
      await bankingService.updateTransaction(tx.id, {
        documentDate: date ?? undefined,
        clearDocumentDate: date === null,
      });
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Das Rechnungsdatum ließ sich nicht setzen.');
    }
  };

  const saveVatSplits = async (tx: BankTransaction, splits: VatSplitRequest[]) => {
    setBusy(true);
    try {
      await bankingService.updateTransaction(tx.id, { vatSplits: splits });
      toast.success(splits.length > 0 ? 'Aufteilung gespeichert.' : 'Aufteilung aufgehoben.');
      setSplittingFor(null);
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Die Aufteilung ließ sich nicht speichern.');
    } finally {
      setBusy(false);
    }
  };

  // ============================================================
  // Kategorien
  // ============================================================

  /** Die Vorlage für eine neue Kategorie. */
  const emptyCategory = (): TransactionCategoryRequest => ({
    name: '',
    kind: 'EXPENSE',
    accountCode: '',
    // 19 % ist der Regelsatz und damit der richtige Startwert; „kein Abzug" ist die Ausnahme
    // und wird bewusst gewählt.
    defaultVatRatePercent: 19,
    defaultBusinessArea: '',
    sortOrder: 100,
    active: true,
  });

  const loadAllCategories = useCallback(async () => {
    try {
      setAllCategories(await bankingService.getCategories(true));
    } catch {
      setAllCategories([]);
    }
  }, []);

  const saveCategory = async () => {
    if (!categoryForm) return;
    if (!categoryForm.name.trim()) {
      toast.error('Die Kategorie braucht einen Namen.');
      return;
    }
    setBusy(true);
    try {
      const payload: TransactionCategoryRequest = {
        ...categoryForm,
        name: categoryForm.name.trim(),
        accountCode: categoryForm.accountCode?.trim() || null,
        defaultBusinessArea: categoryForm.defaultBusinessArea?.trim() || null,
      };
      if (editingCategoryId !== null) {
        await bankingService.updateCategory(editingCategoryId, payload);
        toast.success('Kategorie gespeichert.');
      } else {
        await bankingService.createCategory(payload);
        toast.success('Kategorie angelegt.');
      }
      setCategoryForm(null);
      setEditingCategoryId(null);
      await Promise.all([refreshAll(), loadAllCategories()]);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? e?.response?.data?.error ?? 'Die Kategorie ließ sich nicht speichern.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Schaltet eine Kategorie ab — gelöscht wird nie.
   *
   * An ihr hängen Umsätze, und die sollen ihre Vorgeschichte behalten: eine gelöschte Kategorie
   * ließe Buchungen zurück, die einmal zugeordnet waren und es plötzlich nicht mehr sind. Sie
   * verschwindet nur aus der Auswahl; bereits zugeordnete Umsätze bleiben, wie sie sind.
   */
  const deactivateCategory = async (category: TransactionCategory) => {
    if (!window.confirm(
      `„${category.name}" abschalten? Die Kategorie verschwindet aus der Auswahl. ` +
      'Bereits zugeordnete Umsätze behalten sie.'
    )) return;
    setBusy(true);
    try {
      await bankingService.deleteCategory(category.id);
      toast.success('Kategorie abgeschaltet.');
      await Promise.all([refreshAll(), loadAllCategories()]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Die Kategorie ließ sich nicht abschalten.');
    } finally {
      setBusy(false);
    }
  };

  /** Eine abgeschaltete Kategorie wieder in die Auswahl holen. */
  const reactivateCategory = async (category: TransactionCategory) => {
    setBusy(true);
    try {
      await bankingService.updateCategory(category.id, {
        name: category.name,
        kind: category.kind,
        accountCode: category.accountCode ?? null,
        defaultVatRatePercent: category.defaultVatRatePercent ?? null,
        defaultBusinessArea: category.defaultBusinessArea ?? null,
        sortOrder: category.sortOrder,
        active: true,
      });
      await Promise.all([refreshAll(), loadAllCategories()]);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Die Kategorie ließ sich nicht aktivieren.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ordnet einen Ausgang einem Vorhaben zu.
   *
   * Von Hand gesetzt heißt: bleibt auch bei einem späteren Kategoriewechsel stehen — dieselbe
   * Regel wie beim Steuersatz.
   */
  const setTransactionArea = async (tx: BankTransaction, area: string | null) => {
    try {
      await bankingService.updateTransaction(tx.id, {
        businessArea: area ?? undefined,
        clearBusinessArea: area === null,
      });
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Das Vorhaben ließ sich nicht setzen.');
    }
  };

  const setVatRate = async (tx: BankTransaction, rate: number | null) => {
    try {
      await bankingService.updateTransaction(tx.id, {
        vatRatePercent: rate ?? undefined,
        clearVat: rate === null,
      });
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Der Steuersatz ließ sich nicht setzen.');
    }
  };

  const assignInvoice = async (tx: BankTransaction, invoiceId: number, markPaid: boolean) => {
    setBusy(true);
    try {
      await bankingService.updateTransaction(tx.id, { invoiceId, markInvoicePaid: markPaid });
      toast.success(markPaid ? 'Zugeordnet und als bezahlt gebucht.' : 'Zugeordnet.');
      setMatchingFor(null);
      setLastImport(prev => prev
        ? { ...prev, suggestions: prev.suggestions.filter(s => s.transactionId !== tx.id) }
        : prev);
      await refreshAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Die Zuordnung schlug fehl.');
    } finally {
      setBusy(false);
    }
  };

  const clearInvoice = async (tx: BankTransaction) => {
    try {
      await bankingService.updateTransaction(tx.id, { clearInvoice: true });
      await refreshAll();
    } catch {
      toast.error('Die Zuordnung ließ sich nicht lösen.');
    }
  };

  const openMatchDialog = async (tx: BankTransaction) => {
    setMatchingFor(tx);
    if (candidates.length === 0) {
      try {
        setCandidates(await bankingService.getInvoiceCandidates());
      } catch {
        toast.error('Die Rechnungen ließen sich nicht laden.');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  return (
    <div>
      <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onFileChosen} />

      <PageHeader
        title="Konten"
        subtitle="Kontoumsätze einlesen, zuordnen und auswerten"
        action={
          <button
            onClick={() => { setEditingAccountId(null); setAccountForm(emptyAccount()); }}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
            + Konto anlegen
          </button>
        }
      />

      {accounts.length === 0 ? (
        <div className="rounded-xl p-6 mb-6 text-sm"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)', color: 'var(--text-muted)' }}>
          Noch kein Konto angelegt. Leg zuerst dein Geschäftskonto an — und, wenn du für die
          Steuern zurücklegst, ein zweites Konto mit dem Zweck „Steuerrücklage“.
        </div>
      ) : (
        <>
          <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {accounts.map(a => (
              <AccountCard
                key={a.id}
                account={a}
                onImport={() => startImport(a.id)}
                onEdit={() => {
                  setEditingAccountId(a.id);
                  setAccountForm({
                    name: a.name, iban: a.iban, bic: a.bic ?? '', bankName: a.bankName ?? '',
                    purpose: a.purpose, currency: a.currency,
                    openingBalanceCents: a.openingBalanceCents,
                    openingBalanceDate: a.openingBalanceDate ?? null,
                    notes: a.notes ?? '', active: a.active,
                  });
                }}
              />
            ))}
            {summary && accounts.length > 1 && (
              <div className="rounded-xl p-4"
                style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
                <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                  Zusammen
                </div>
                <Line label="verfügbar" value={euro(summary.operatingBalanceCents)} strong />
                <Line label="zurückgelegt" value={euro(summary.reserveBalanceCents)} />
                <Line label="gesamt" value={euro(summary.totalBalanceCents)} />
              </div>
            )}
          </div>

          {summary && (summary.uncategorizedCount > 0 || summary.unmatchedIncomingCount > 0
            || summary.missingVatCount > 0 || summary.missingReceiptCount > 0) && (
            <div className="rounded-xl p-4 mb-6 text-sm flex flex-wrap gap-x-6 gap-y-2"
              style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Zu erledigen:</span>
              {summary.uncategorizedCount > 0 && (
                <button className="underline" style={{ color: 'var(--text-primary)' }}
                  onClick={() => setFilter(f => ({ ...f, uncategorized: true, unmatched: false, missingVat: false }))}>
                  {summary.uncategorizedCount} ohne Kategorie
                </button>
              )}
              {summary.unmatchedIncomingCount > 0 && (
                <button className="underline" style={{ color: 'var(--text-primary)' }}
                  onClick={() => setFilter(f => ({ ...f, unmatched: true, uncategorized: false, missingVat: false }))}>
                  {summary.unmatchedIncomingCount} Eingänge ohne Rechnung
                </button>
              )}
              {summary.missingVatCount > 0 && (
                <button className="underline" style={{ color: 'var(--text-primary)' }}
                  onClick={() => setFilter(f => ({ ...f, missingVat: true, uncategorized: false, unmatched: false }))}>
                  {summary.missingVatCount} Ausgänge ohne Steuersatz
                </button>
              )}
              {summary.missingReceiptCount > 0 && (
                <button className="underline" style={{ color: 'var(--text-primary)' }}
                  onClick={() => setFilter(f => ({
                    ...f, missingReceipt: true, missingVat: false, uncategorized: false, unmatched: false,
                  }))}>
                  {summary.missingReceiptCount} Ausgänge ohne Beleg
                </button>
              )}
            </div>
          )}
        </>
      )}

      {lastImport && (
        <ImportReport
          result={lastImport}
          transactions={transactions}
          onDismiss={() => setLastImport(null)}
          onRevert={() => revertImport(lastImport)}
          onAssign={(s) => {
            const tx = transactions.find(t => t.id === s.transactionId);
            if (tx) openMatchDialog(tx);
          }}
        />
      )}

      {accounts.length > 0 && (
        <>
          {/* Filter in einer Reihe über der Tabelle */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              value={filter.accountId ?? ''}
              onChange={e => setFilter(f => ({ ...f, accountId: e.target.value ? Number(e.target.value) : undefined }))}
              className="px-3 py-1.5 rounded-lg text-sm border"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              <option value="">Alle Konten</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>

            <select
              value={filter.categoryId ?? ''}
              onChange={e => setFilter(f => ({ ...f, categoryId: e.target.value ? Number(e.target.value) : undefined }))}
              className="px-3 py-1.5 rounded-lg text-sm border"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
              <option value="">Alle Kategorien</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <FilterChip active={!!filter.uncategorized}
              onClick={() => setFilter(f => ({ ...f, uncategorized: !f.uncategorized }))}>
              ohne Kategorie
            </FilterChip>
            <FilterChip active={!!filter.unmatched}
              onClick={() => setFilter(f => ({ ...f, unmatched: !f.unmatched }))}>
              Eingang ohne Rechnung
            </FilterChip>
            <FilterChip active={!!filter.missingVat}
              onClick={() => setFilter(f => ({ ...f, missingVat: !f.missingVat }))}>
              ohne Steuersatz
            </FilterChip>
            <FilterChip active={!!filter.missingReceipt}
              onClick={() => setFilter(f => ({ ...f, missingReceipt: !f.missingReceipt }))}>
              ohne Beleg
            </FilterChip>

            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Suche in Empfänger und Verwendungszweck"
              className="px-3 py-1.5 rounded-lg text-sm border flex-1 min-w-[220px]"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
            />

            {(filter.accountId || filter.categoryId || filter.uncategorized || filter.unmatched
              || filter.missingVat || filter.missingReceipt || search) && (
              <button className="text-sm underline" style={{ color: 'var(--text-muted)' }}
                onClick={() => { setFilter({ limit: 300 }); setSearch(''); }}>
                zurücksetzen
              </button>
            )}
          </div>

          {/* Einmal im Dokument, von jeder Zeile der Spalte „Vorhaben" genutzt. */}
          <datalist id="banking-business-areas">
            {businessAreas.map(a => <option key={a} value={a} />)}
          </datalist>

          <div className="rounded-xl overflow-hidden mb-6"
            style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
            {transactions.length === 0 ? (
              <p className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>
                Keine Umsätze für diese Auswahl.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead style={{ color: 'var(--text-muted)' }}>
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium">Datum</th>
                    <th className="px-4 py-2 font-medium">Empfänger / Auftraggeber</th>
                    <th className="px-4 py-2 font-medium">Verwendungszweck</th>
                    <th className="px-4 py-2 font-medium text-right">Betrag</th>
                    <th className="px-4 py-2 font-medium text-right">MwSt</th>
                    <th className="px-4 py-2 font-medium">Kategorie</th>
                    <th className="px-4 py-2 font-medium">Vorhaben</th>
                    <th className="px-4 py-2 font-medium">Rechnung / Beleg</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => (
                    <tr key={tx.id} className="border-t align-top" style={{ borderColor: 'var(--surface-border)' }}>
                      <td className="px-4 py-2 whitespace-nowrap nums-tabular" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(tx.bookingDate)}
                        {tx.amountCents < 0 && (
                          <div className="mt-1">
                            <input
                              type="date"
                              value={tx.documentDate ?? ''}
                              onChange={e => setDocumentDate(tx, e.target.value || null)}
                              title="Rechnungsdatum — steuert, in welches Quartal die Vorsteuer fällt. Leer: wie das Buchungsdatum."
                              className="px-1 py-0.5 rounded text-[10px] border nums-tabular"
                              style={{
                                background: 'var(--input-bg)',
                                borderColor: quarterOf(tx.documentDate) !== null
                                  && quarterOf(tx.documentDate) !== quarterOf(tx.bookingDate)
                                  ? 'var(--badge-amber-text)' : 'var(--surface-border)',
                                color: tx.documentDate ? 'var(--text-primary)' : 'var(--text-muted)',
                              }}
                            />
                            {quarterOf(tx.documentDate) !== null
                              && quarterOf(tx.documentDate) !== quarterOf(tx.bookingDate) && (
                              <div className="text-[10px]" style={{ color: 'var(--badge-amber-text)' }}>
                                Vorsteuer in {quarterOf(tx.documentDate)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2" style={{ color: 'var(--text-primary)' }}>
                        {tx.counterparty || '—'}
                        {accounts.length > 1 && (
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{tx.accountName}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 max-w-xs" style={{ color: 'var(--text-muted)' }}>
                        {tx.reference || '—'}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap nums-tabular font-semibold"
                        style={{ color: tx.amountCents < 0 ? 'var(--text-primary)' : 'var(--badge-green-text)' }}>
                        {euro(tx.amountCents)}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {tx.amountCents < 0 ? (
                          <>
                            {tx.vatSplits.length > 0 ? (
                              <button onClick={() => setSplittingFor(tx)}
                                className="px-1.5 py-1 rounded-lg text-xs border"
                                style={{
                                  background: 'var(--input-bg)', borderColor: 'var(--surface-border)',
                                  color: 'var(--text-primary)',
                                }}
                                title={tx.vatSplits.map(v => `${euro(v.grossCents)} zu ${v.vatRatePercent} %`).join('\n')}>
                                {tx.vatSplits.map(v => `${v.vatRatePercent}%`).join(' + ')}
                              </button>
                            ) : (
                              <select
                                value={tx.vatRatePercent ?? ''}
                                onChange={e => setVatRate(tx, e.target.value === '' ? null : Number(e.target.value))}
                                className="px-1.5 py-1 rounded-lg text-xs border nums-tabular"
                                style={{
                                  background: 'var(--input-bg)',
                                  borderColor: tx.vatRatePercent == null
                                    ? 'var(--badge-amber-text)' : 'var(--surface-border)',
                                  color: 'var(--text-primary)',
                                }}>
                                <option value="">—</option>
                                {VAT_RATES.map(r => <option key={r} value={r}>{r} %</option>)}
                              </select>
                            )}
                            <div className="text-[10px] mt-0.5 nums-tabular" style={{ color: 'var(--text-muted)' }}>
                              {tx.inputVatCents > 0 && <>{euro(tx.inputVatCents)} VSt · </>}
                              <button onClick={() => setSplittingFor(tx)} className="underline">
                                {tx.vatSplits.length > 0 ? 'ändern' : 'aufteilen'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={tx.categoryId ?? ''}
                          onChange={e => setCategory(tx, e.target.value ? Number(e.target.value) : null)}
                          className="px-2 py-1 rounded-lg text-xs border w-full"
                          style={{
                            background: 'var(--input-bg)',
                            borderColor: tx.categoryId ? 'var(--surface-border)' : 'var(--badge-amber-text)',
                            color: 'var(--text-primary)',
                          }}>
                          <option value="">— wählen —</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {tx.categoryAuto && (
                          <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                            automatisch erkannt
                          </div>
                        )}
                      </td>
                      {/* Welchem Vorhaben diese Ausgabe zugerechnet wird — erst damit lässt sich
                          dem Erlös aus den Rechnungen die Ausgabe gegenüberstellen. Nur bei
                          Ausgängen: ein Zahlungseingang gehört zu seiner Rechnung, und die trägt
                          die Zuordnung schon; ihn hier ein zweites Mal zuzuordnen hieße, denselben
                          Betrag zweimal zu zählen.
                          Freitext mit Vorschlägen, damit sich ein neues Vorhaben auch von der
                          Kostenseite anlegen lässt — oft ist die erste Serverrechnung da, bevor
                          die erste Rechnung rausgeht. */}
                      <td className="px-4 py-2">
                        {tx.amountCents < 0 ? (
                          <>
                            <input
                              list="banking-business-areas"
                              type="text"
                              defaultValue={tx.businessArea ?? ''}
                              placeholder="—"
                              onBlur={e => {
                                const next = e.target.value.trim() === '' ? null : e.target.value.trim();
                                if (next !== (tx.businessArea ?? null)) setTransactionArea(tx, next);
                              }}
                              className="px-2 py-1 rounded-lg text-xs border w-full"
                              style={{
                                background: 'var(--input-bg)',
                                borderColor: 'var(--surface-border)',
                                color: 'var(--text-primary)',
                              }}
                            />
                            {tx.businessAreaAuto && tx.businessArea && (
                              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                aus der Kategorie
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {tx.invoiceId ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="green">{tx.invoiceNumber}</Badge>
                            <button onClick={() => clearInvoice(tx)} className="text-xs underline"
                              style={{ color: 'var(--text-muted)' }}>lösen</button>
                          </div>
                        ) : tx.amountCents > 0 && tx.categoryKind !== 'TRANSFER' ? (
                          <button onClick={() => openMatchDialog(tx)} className="text-xs underline"
                            style={{ color: 'var(--text-primary)' }}>zuordnen</button>
                        ) : tx.amountCents < 0 && tx.documentCount > 0 ? (
                          <Link to={`/documents?transactionId=${tx.id}`} title="Belege zu diesem Umsatz">
                            <Badge variant="green">
                              {tx.documentCount === 1 ? 'Beleg' : `${tx.documentCount} Belege`}
                            </Badge>
                          </Link>
                        ) : tx.amountCents < 0 && !['TRANSFER', 'TAX', 'PRIVATE'].includes(tx.categoryKind ?? '') ? (
                          <Link to={`/documents?transactionId=${tx.id}`} className="text-xs underline"
                            style={{ color: 'var(--badge-amber-text)' }}>Beleg fehlt</Link>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {summary && (
            <div className="rounded-xl p-5"
              style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Auswertung {summary.year}
                </h2>
                <div className="flex gap-1">
                  {summary.availableYears.map(y => (
                    <button key={y}
                      onClick={async () => setSummary(await bankingService.getSummary(y))}
                      className="px-2.5 py-1 rounded-lg text-xs font-semibold border"
                      style={{
                        borderColor: y === summary.year ? 'transparent' : 'var(--surface-border)',
                        background: y === summary.year ? 'var(--sidebar-link-active-bg)' : 'transparent',
                        color: y === summary.year ? '#ffffff' : 'var(--text-muted)',
                      }}>
                      {y}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-x-10 gap-y-3 mb-5 nums-tabular">
                <Figure label="eingegangen" value={euro(summary.incomingCents)} />
                <Figure label="ausgegeben" value={euro(summary.outgoingCents)} />
                <Figure label="Differenz" value={euro(summary.incomingCents - summary.outgoingCents)} strong />
              </div>
              <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                Umbuchungen zwischen den eigenen Konten sind herausgerechnet — sonst zählte jeder
                Griff in die Rücklage doppelt.
              </p>

              <VatQuarters summary={summary} />

              <button onClick={() => setShowCategories(v => !v)} className="text-xs underline"
                style={{ color: 'var(--text-muted)' }}>
                {showCategories ? 'Kategorien ausblenden' : 'Nach Kategorien aufschlüsseln'}
              </button>

              {showCategories && (
                <table className="w-full text-xs mt-3 nums-tabular">
                  <thead style={{ color: 'var(--text-muted)' }}>
                    <tr className="text-left">
                      <th className="py-1">Kategorie</th>
                      <th className="py-1">Art</th>
                      <th className="py-1 text-right">Buchungen</th>
                      <th className="py-1 text-right">Summe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.categories.map(c => (
                      <tr key={c.categoryId ?? 'none'} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                        <td className="py-1" style={{ color: 'var(--text-primary)' }}>{c.name}</td>
                        <td className="py-1" style={{ color: 'var(--text-muted)' }}>
                          {c.kind ? KIND_LABEL[c.kind] : '—'}
                        </td>
                        <td className="py-1 text-right">{c.transactionCount}</td>
                        <td className="py-1 text-right">{euro(c.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {accounts.length > 0 && imports.length > 0 && (
        <div className="rounded-xl p-5 mt-6"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Eingelesene Dateien
            </h2>
            <button onClick={() => setShowImports(v => !v)} className="text-xs underline"
              style={{ color: 'var(--text-muted)' }}>
              {showImports ? 'ausblenden' : `${imports.length} anzeigen`}
            </button>
          </div>

          {showImports && (
            <>
              <p className="text-xs mt-3 mb-2" style={{ color: 'var(--text-muted)' }}>
                Ein falsch eingelesener Auszug lässt sich hier zurücknehmen — die Umsätze dieses
                Laufs verschwinden dann samt Kategorien und Zuordnungen. Rechnungen, die dabei auf
                „bezahlt“ gingen, bleiben bezahlt.
              </p>
              <table className="w-full text-xs nums-tabular">
                <thead style={{ color: 'var(--text-muted)' }}>
                  <tr className="text-left">
                    <th className="py-1">Datei</th>
                    <th className="py-1">Konto</th>
                    <th className="py-1">Zeitraum</th>
                    <th className="py-1 text-right">neu</th>
                    <th className="py-1 text-right">doppelt</th>
                    <th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map(imp => (
                    <tr key={imp.id} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                      <td className="py-1" style={{ color: 'var(--text-primary)' }}>
                        {imp.fileName}
                        <div style={{ color: 'var(--text-muted)' }}>
                          {imp.createdAt ? new Date(imp.createdAt).toLocaleString('de-DE') : ''} · {imp.importedBy}
                        </div>
                      </td>
                      <td className="py-1" style={{ color: 'var(--text-muted)' }}>{imp.accountName}</td>
                      <td className="py-1" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(imp.periodFrom)} – {formatDate(imp.periodTo)}
                      </td>
                      <td className="py-1 text-right">{imp.rowsImported}</td>
                      <td className="py-1 text-right" style={{ color: 'var(--text-muted)' }}>{imp.rowsDuplicate}</td>
                      <td className="py-1 text-right">
                        <button disabled={busy} onClick={() => revertImport(imp)}
                          className="underline disabled:opacity-50" style={{ color: 'var(--badge-red-text)' }}>
                          zurücknehmen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Kategorien pflegen.
          Sie steuern drei Dinge auf einmal: wie ein Umsatz in der Auswertung erscheint, welchen
          Steuersatz ein Ausgang beim Import vorgeschlagen bekommt (`defaultVatRatePercent`) und
          welchem Vorhaben er zugerechnet wird (`defaultBusinessArea`). Bis hierher gab es die
          Pflege nur über die API — angelegt wurden Kategorien beim Import, geändert gar nicht.
          Eingeklappt, weil es die seltenere Arbeit ist: im Alltag ordnet man zu, nicht um. */}
      {accounts.length > 0 && (
        <div className="rounded-xl p-5 mt-6"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Kategorien
            </h2>
            <button
              onClick={() => {
                const next = !showCategoryAdmin;
                setShowCategoryAdmin(next);
                if (next) loadAllCategories();
              }}
              className="text-xs underline"
              style={{ color: 'var(--text-muted)' }}>
              {showCategoryAdmin ? 'ausblenden' : `${categories.length} pflegen`}
            </button>
          </div>

          {showCategoryAdmin && (
            <>
              <p className="text-xs mt-3 mb-3" style={{ color: 'var(--text-muted)' }}>
                Die Kategorie sagt, <em>was</em> ein Umsatz war. Steuersatz und Vorhaben sind
                Startwerte: sie greifen beim Import und bei einem Kategoriewechsel, solange am
                Umsatz nichts von Hand gesetzt wurde.
              </p>

              <table className="w-full text-xs">
                <thead style={{ color: 'var(--text-muted)' }}>
                  <tr className="text-left">
                    <th className="py-1">Name</th>
                    <th className="py-1">Art</th>
                    <th className="py-1">SKR03</th>
                    <th className="py-1 text-right">USt-Vorgabe</th>
                    <th className="py-1">Vorhaben</th>
                    <th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {(showInactiveCategories ? allCategories : allCategories.filter(c => c.active))
                    .map(c => (
                    <tr key={c.id} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                      <td className="py-1.5" style={{ color: c.active ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {c.name}
                        {!c.active && <span className="ml-2">(abgeschaltet)</span>}
                      </td>
                      <td className="py-1.5" style={{ color: 'var(--text-muted)' }}>{KIND_LABEL[c.kind]}</td>
                      <td className="py-1.5 nums-tabular" style={{ color: 'var(--text-muted)' }}>
                        {c.accountCode || '—'}
                      </td>
                      <td className="py-1.5 text-right nums-tabular" style={{ color: 'var(--text-muted)' }}>
                        {c.defaultVatRatePercent == null ? 'kein Abzug' : `${c.defaultVatRatePercent} %`}
                      </td>
                      <td className="py-1.5" style={{ color: 'var(--text-muted)' }}>
                        {c.defaultBusinessArea || '—'}
                      </td>
                      <td className="py-1.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => {
                            setEditingCategoryId(c.id);
                            setCategoryForm({
                              name: c.name, kind: c.kind,
                              accountCode: c.accountCode ?? '',
                              defaultVatRatePercent: c.defaultVatRatePercent ?? null,
                              defaultBusinessArea: c.defaultBusinessArea ?? '',
                              sortOrder: c.sortOrder, active: c.active,
                            });
                          }}
                          className="underline" style={{ color: 'var(--text-primary)' }}>
                          bearbeiten
                        </button>
                        {c.active ? (
                          <button disabled={busy} onClick={() => deactivateCategory(c)}
                            className="underline ml-3 disabled:opacity-50"
                            style={{ color: 'var(--badge-red-text)' }}>
                            abschalten
                          </button>
                        ) : (
                          <button disabled={busy} onClick={() => reactivateCategory(c)}
                            className="underline ml-3 disabled:opacity-50"
                            style={{ color: 'var(--text-muted)' }}>
                            wieder anschalten
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                <button
                  onClick={() => { setEditingCategoryId(null); setCategoryForm(emptyCategory()); }}
                  className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-xs font-semibold">
                  + Neue Kategorie
                </button>
                {allCategories.some(c => !c.active) && (
                  <button onClick={() => setShowInactiveCategories(v => !v)}
                    className="text-xs underline" style={{ color: 'var(--text-muted)' }}>
                    {showInactiveCategories ? 'Abgeschaltete ausblenden' : 'Abgeschaltete anzeigen'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {categoryForm && (
        <CategoryDialog
          form={categoryForm}
          isEdit={editingCategoryId !== null}
          busy={busy}
          businessAreas={businessAreas}
          onChange={setCategoryForm}
          onCancel={() => { setCategoryForm(null); setEditingCategoryId(null); }}
          onSave={saveCategory}
        />
      )}

      {accountForm && (
        <AccountDialog
          form={accountForm}
          isEdit={editingAccountId !== null}
          busy={busy}
          onChange={setAccountForm}
          onCancel={() => { setAccountForm(null); setEditingAccountId(null); }}
          onSave={saveAccount}
        />
      )}

      {splittingFor && (
        <VatSplitDialog
          transaction={splittingFor}
          busy={busy}
          onCancel={() => setSplittingFor(null)}
          onSave={splits => saveVatSplits(splittingFor, splits)}
        />
      )}

      {matchingFor && (
        <MatchDialog
          transaction={matchingFor}
          candidates={candidates}
          busy={busy}
          onCancel={() => setMatchingFor(null)}
          onAssign={assignInvoice}
        />
      )}
    </div>
  );
};

// ============================================================
// Bausteine
// ============================================================

/**
 * Umsatzsteuer und Vorsteuer je Quartal — die Zahlen der Voranmeldung.
 *
 * Links, was du deinen Kunden berechnet hast, rechts, was dir Lieferanten berechnet
 * haben, und die Differenz ist das, was ans Finanzamt geht (oder zurückkommt).
 *
 * Auf dem Konto steht immer der Bruttobetrag, deshalb wird die Steuer herausgerechnet:
 * `brutto × Satz / (100 + Satz)`. Einsortiert wird nach dem Rechnungsdatum, nicht nach
 * dem Zahlungszeitpunkt — eine Dezember-Rechnung, die im Januar vom Konto geht, gehört
 * mit ihrer Vorsteuer noch ins vierte Quartal. Umbuchungen zwischen eigenen Konten und
 * Steuerzahlungen bleiben draußen, daraus entsteht keine Vorsteuer.
 */
const VatQuarters: React.FC<{ summary: BankingSummary }> = ({ summary }) => {
  const [showRates, setShowRates] = useState(false);
  const quarters = summary.vatQuarters.filter(q => q.transactionCount > 0 || q.invoiceCount > 0);
  if (quarters.length === 0) return null;

  const total = quarters.reduce((sum, q) => sum + q.inputVatCents, 0);
  const totalSales = quarters.reduce((sum, q) => sum + q.salesVatCents, 0);
  const totalPayable = totalSales - total;

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Umsatzsteuer {summary.year}
        </h3>
        <button onClick={() => setShowRates(v => !v)} className="text-xs underline"
          style={{ color: 'var(--text-muted)' }}>
          {showRates ? 'Sätze ausblenden' : 'nach Steuersätzen'}
        </button>
      </div>

      <table className="w-full text-xs nums-tabular">
        <thead style={{ color: 'var(--text-muted)' }}>
          <tr className="text-left">
            <th className="py-1">Quartal</th>
            <th className="py-1 text-right">USt aus Rechnungen</th>
            <th className="py-1 text-right">Vorsteuer</th>
            <th className="py-1 text-right">ans Finanzamt</th>
            <th className="py-1 text-right">Rechn.</th>
            <th className="py-1 text-right">Buch.</th>
          </tr>
        </thead>
        <tbody>
          {quarters.map(q => (
            <React.Fragment key={q.quarter}>
              <tr className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                <td className="py-1 font-semibold" style={{ color: 'var(--text-primary)' }}>Q{q.quarter}</td>
                <td className="py-1 text-right">{euro(q.salesVatCents)}</td>
                <td className="py-1 text-right">− {euro(q.inputVatCents)}</td>
                <td className="py-1 text-right font-bold"
                  style={{ color: q.payableCents < 0 ? 'var(--badge-green-text)' : 'var(--text-primary)' }}>
                  {euro(q.payableCents)}
                </td>
                <td className="py-1 text-right" style={{ color: 'var(--text-muted)' }}>{q.invoiceCount}</td>
                <td className="py-1 text-right" style={{ color: 'var(--text-muted)' }}>
                  {q.transactionCount}
                  {q.shiftedCount > 0 && (
                    <span title={`${q.shiftedCount} davon über das Rechnungsdatum hierher gezogen`}>
                      {' '}({q.shiftedCount}↔)
                    </span>
                  )}
                </td>
              </tr>
              {showRates && (
                <>
                  <tr style={{ color: 'var(--text-muted)' }}>
                    <td className="py-0.5 pl-4" colSpan={2}>
                      Umsatz netto {euro(q.salesNetCents)}
                    </td>
                    <td className="py-0.5 text-right" colSpan={4}>
                      Ausgaben brutto {euro(q.grossExpenseCents)} · netto{' '}
                      {euro(q.grossExpenseCents - q.inputVatCents)}
                    </td>
                  </tr>
                  {q.rates.map(r => (
                    <tr key={`${q.quarter}-${r.ratePercent}`} style={{ color: 'var(--text-muted)' }}>
                      <td className="py-0.5 pl-4">Vorsteuer {r.ratePercent} %</td>
                      <td className="py-0.5 text-right">{euro(r.grossCents)} brutto</td>
                      <td className="py-0.5 text-right">− {euro(r.inputVatCents)}</td>
                      <td className="py-0.5"></td>
                      <td className="py-0.5"></td>
                      <td className="py-0.5 text-right">{r.transactionCount}</td>
                    </tr>
                  ))}
                </>
              )}
            </React.Fragment>
          ))}
          <tr className="border-t-2" style={{ borderColor: 'var(--surface-border)' }}>
            <td className="py-1 font-bold" style={{ color: 'var(--text-primary)' }}>Jahr</td>
            <td className="py-1 text-right font-semibold">{euro(totalSales)}</td>
            <td className="py-1 text-right font-semibold">− {euro(total)}</td>
            <td className="py-1 text-right font-bold"
              style={{ color: totalPayable < 0 ? 'var(--badge-green-text)' : 'var(--text-primary)' }}>
              {euro(totalPayable)}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
        Beide Seiten nach dem Rechnungsdatum (Soll-Versteuerung): links die Umsatzsteuer
        deiner gestellten Rechnungen, rechts die Vorsteuer deiner Ausgaben. Bei Ausgaben
        ohne gesetztes Rechnungsdatum zählt das Buchungsdatum.
        {quarters.some(q => q.shiftedCount > 0) &&
          ' ↔ markiert Buchungen, die über ihr Rechnungsdatum in dieses Quartal gehören.'}
        {' '}Ein negativer Betrag heißt Erstattung.
      </p>

      {summary.missingVatCount > 0 && (
        <p className="text-xs mt-1" style={{ color: 'var(--badge-amber-text)' }}>
          {summary.missingVatCount} Ausgänge haben noch keinen Steuersatz und fehlen in dieser Rechnung.
        </p>
      )}
    </div>
  );
};

const Line: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex justify-between items-baseline text-sm py-0.5 nums-tabular">
    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
    <span className={strong ? 'font-bold' : ''} style={{ color: 'var(--text-primary)' }}>{value}</span>
  </div>
);

const Figure: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div>
    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</div>
    <div className={`${strong ? 'text-2xl font-extrabold' : 'text-lg font-bold'} tracking-tight`}
      style={{ color: 'var(--text-primary)' }}>
      {value}
    </div>
  </div>
);

const FilterChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> =
  ({ active, onClick, children }) => (
    <button onClick={onClick}
      className="px-3 py-1.5 rounded-lg text-sm font-medium border"
      style={{
        borderColor: active ? 'transparent' : 'var(--surface-border)',
        background: active ? 'var(--sidebar-link-active-bg)' : 'transparent',
        color: active ? '#ffffff' : 'var(--text-muted)',
      }}>
      {children}
    </button>
  );

const AccountCard: React.FC<{ account: BankAccount; onImport: () => void; onEdit: () => void }> =
  ({ account, onImport, onEdit }) => (
    <div className="rounded-xl p-4" style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-bold" style={{ color: 'var(--text-primary)' }}>{account.name}</div>
          <div className="text-[11px] nums-tabular" style={{ color: 'var(--text-muted)' }}>
            {formatIban(account.iban)}
          </div>
        </div>
        <Badge variant={account.purpose === 'OPERATING' ? 'purple' : 'amber'}>
          {PURPOSE_LABEL[account.purpose]}
        </Badge>
      </div>
      <div className="text-2xl font-extrabold tracking-tight mb-1" style={{ color: 'var(--text-primary)' }}>
        {euro(account.balanceCents)}
      </div>
      <div className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
        {account.transactionCount} Umsätze
        {account.lastTransactionDate && ` · zuletzt ${formatDate(account.lastTransactionDate)}`}
      </div>
      <div className="flex gap-3 text-xs">
        <button onClick={onImport} className="font-semibold underline" style={{ color: 'var(--text-primary)' }}>
          Umsätze importieren
        </button>
        <button onClick={onEdit} className="underline" style={{ color: 'var(--text-muted)' }}>
          bearbeiten
        </button>
      </div>
    </div>
  );

const ImportReport: React.FC<{
  result: BankImportResult;
  transactions: BankTransaction[];
  onDismiss: () => void;
  onRevert: () => void;
  onAssign: (s: MatchSuggestion) => void;
}> = ({ result, onDismiss, onRevert, onAssign }) => (
  <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
        <strong>{result.fileName}</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          {' '}({result.format}) · {result.rowsTotal} Zeilen gelesen · {result.rowsImported} neu
          {result.rowsDuplicate > 0 && ` · ${result.rowsDuplicate} lagen schon vor`}
          {result.invoicesMatched > 0 && ` · ${result.invoicesMatched} Rechnungen auf bezahlt gesetzt`}
          {(result.receiptsMatched ?? 0) > 0 && ` · ${result.receiptsMatched} Belege zugeordnet`}
        </span>
      </div>
      <div className="flex gap-3 whitespace-nowrap">
        {result.id != null && (
          <button onClick={onRevert} className="text-xs underline" style={{ color: 'var(--badge-red-text)' }}>
            zurücknehmen
          </button>
        )}
        <button onClick={onDismiss} className="text-xs underline" style={{ color: 'var(--text-muted)' }}>
          schließen
        </button>
      </div>
    </div>

    {result.warnings.map((w, i) => (
      <p key={i} className="text-xs" style={{ color: 'var(--badge-amber-text)' }}>{w}</p>
    ))}

    {result.suggestions.length > 0 && (
      <div className="mt-3">
        <div className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Nicht sicher zuzuordnen — bitte prüfen:
        </div>
        <ul className="space-y-1.5">
          {result.suggestions.map(s => (
            <li key={s.transactionId} className="text-xs flex flex-wrap items-baseline gap-x-2"
              style={{ color: 'var(--text-muted)' }}>
              <span className="nums-tabular">{formatDate(s.bookingDate)}</span>
              <span style={{ color: 'var(--text-primary)' }}>{s.counterparty}</span>
              <span className="nums-tabular font-semibold" style={{ color: 'var(--text-primary)' }}>
                {euro(s.amountCents)}
              </span>
              <span>— {s.reason}</span>
              <button onClick={() => onAssign(s)} className="underline" style={{ color: 'var(--text-primary)' }}>
                zuordnen
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const AccountDialog: React.FC<{
  form: BankAccountRequest;
  isEdit: boolean;
  busy: boolean;
  onChange: (f: BankAccountRequest) => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ form, isEdit, busy, onChange, onCancel, onSave }) => {
  const field = 'w-full px-3 py-2 rounded-lg text-sm border';
  const fieldStyle = {
    background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)',
  };
  return (
    <ModalOverlay ariaLabelledBy="account-dialog-title">
      <div className="min-h-full flex items-start justify-center p-4 py-10">
        <div className="w-full max-w-lg rounded-xl p-6"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <h2 id="account-dialog-title" className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {isEdit ? 'Konto bearbeiten' : 'Konto anlegen'}
          </h2>

          <div className="space-y-3">
            <Field label="Bezeichnung">
              <input className={field} style={fieldStyle} value={form.name}
                onChange={e => onChange({ ...form, name: e.target.value })}
                placeholder="Geschäftskonto" />
            </Field>
            <Field label="IBAN">
              <input className={`${field} nums-tabular`} style={fieldStyle} value={form.iban}
                onChange={e => onChange({ ...form, iban: e.target.value })}
                placeholder="DE00 0000 0000 0000 0000 00" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bank">
                <input className={field} style={fieldStyle} value={form.bankName ?? ''}
                  onChange={e => onChange({ ...form, bankName: e.target.value })} />
              </Field>
              <Field label="BIC">
                <input className={field} style={fieldStyle} value={form.bic ?? ''}
                  onChange={e => onChange({ ...form, bic: e.target.value })} />
              </Field>
            </div>
            <Field label="Zweck" hint="Eine Rücklage zählt nicht zum verfügbaren Geld.">
              <select className={field} style={fieldStyle} value={form.purpose}
                onChange={e => onChange({ ...form, purpose: e.target.value as AccountPurpose })}>
                <option value="OPERATING">Geschäftskonto</option>
                <option value="TAX_RESERVE">Steuerrücklage</option>
                <option value="SAVINGS">Sonstige Rücklage</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Saldo vor dem ersten Umsatz"
                hint="Damit stimmt der Kontostand, auch wenn der Auszug später beginnt.">
                <input className={`${field} nums-tabular`} style={fieldStyle}
                  value={centsToInput(form.openingBalanceCents)}
                  onChange={e => onChange({ ...form, openingBalanceCents: inputToCents(e.target.value) })} />
              </Field>
              <Field label="Stichtag dazu">
                <input type="date" className={field} style={fieldStyle}
                  value={form.openingBalanceDate ?? ''}
                  onChange={e => onChange({ ...form, openingBalanceDate: e.target.value || null })} />
              </Field>
            </div>
            <Field label="Notiz">
              <textarea className={field} style={fieldStyle} rows={2} value={form.notes ?? ''}
                onChange={e => onChange({ ...form, notes: e.target.value })} />
            </Field>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-muted)' }}>
              Abbrechen
            </button>
            <button onClick={onSave} disabled={busy}
              className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
              {busy ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

/**
 * Kategorie anlegen oder ändern.
 *
 * Die drei unteren Felder sind **Startwerte**, keine Festlegungen: sie greifen beim Import und
 * bei einem Kategoriewechsel, solange am Umsatz selbst nichts von Hand gesetzt wurde. Deshalb
 * stehen sie unter einer eigenen Zwischenüberschrift — sonst liest sich „19 %" wie eine Aussage
 * über jeden Umsatz dieser Kategorie, und das wäre bei der ersten Auslandsrechnung falsch.
 *
 * Gelöscht wird eine Kategorie nie, nur abgeschaltet: an ihr hängen Buchungen, die ihre
 * Vorgeschichte behalten sollen. Der Schalter dafür steht in der Liste, nicht hier — ein
 * Dialog, in dem man etwas abschaltet und dann noch speichern muss, hat zwei Wege für
 * dieselbe Entscheidung.
 */
const CategoryDialog: React.FC<{
  form: TransactionCategoryRequest;
  isEdit: boolean;
  busy: boolean;
  businessAreas: string[];
  onChange: (f: TransactionCategoryRequest) => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ form, isEdit, busy, businessAreas, onChange, onCancel, onSave }) => {
  const field = 'w-full px-3 py-2 rounded-lg text-sm border';
  const fieldStyle = {
    background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)',
  };
  return (
  <ModalOverlay ariaLabelledBy="category-dialog-title">
    <div className="min-h-full flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-lg rounded-xl p-6"
        style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
        <h2 id="category-dialog-title" className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? 'Kategorie bearbeiten' : 'Kategorie anlegen'}
        </h2>

        <div className="space-y-3">
          <Field label="Name">
            <input className={field} style={fieldStyle} value={form.name}
              onChange={e => onChange({ ...form, name: e.target.value })}
              placeholder="Hosting" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Art"
              hint="Umbuchung und Steuern bleiben aus Cashflow und Vorsteuer heraus.">
              <select className={field} style={fieldStyle} value={form.kind}
                onChange={e => onChange({ ...form, kind: e.target.value as CategoryKind })}>
                {(Object.keys(KIND_LABEL) as CategoryKind[]).map(k => (
                  <option key={k} value={k}>{KIND_LABEL[k]}</option>
                ))}
              </select>
            </Field>
            <Field label="Konto SKR03" hint="Notiz für den Steuerberater, sonst ohne Wirkung.">
              <input className={`${field} nums-tabular`} style={fieldStyle} value={form.accountCode ?? ''}
                onChange={e => onChange({ ...form, accountCode: e.target.value })}
                placeholder="4980" />
            </Field>
          </div>

          <p className="text-xs pt-2" style={{ color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Startwerte</strong> — sie greifen beim
            Import und bei einem Kategoriewechsel, solange am Umsatz nichts von Hand gesetzt wurde.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Steuersatz"
              hint="„Kein Abzug“ für alles ohne Vorsteuer, etwa Versicherungen.">
              <select className={field} style={fieldStyle}
                value={form.defaultVatRatePercent ?? ''}
                onChange={e => onChange({
                  ...form,
                  defaultVatRatePercent: e.target.value === '' ? null : Number(e.target.value),
                })}>
                <option value="">kein Abzug</option>
                {VAT_RATES.map(r => <option key={r} value={r}>{r} %</option>)}
              </select>
            </Field>
            <Field label="Reihenfolge" hint="Kleiner steht weiter oben in der Auswahl.">
              <input type="number" className={`${field} nums-tabular`} style={fieldStyle}
                value={form.sortOrder}
                onChange={e => onChange({ ...form, sortOrder: Number(e.target.value) || 0 })} />
            </Field>
          </div>

          <Field label="Vorhaben"
            hint="Nur, wenn diese Kategorie immer zu einem Vorhaben gehört — leer lassen heißt: von Hand zuordnen.">
            {/* Freitext mit Vorschlägen: ein neues Vorhaben soll sich auch hier anlegen lassen,
                und die Liste schützt davor, dass aus einem zwei werden. */}
            <input list="category-business-areas" className={field} style={fieldStyle}
              value={form.defaultBusinessArea ?? ''}
              onChange={e => onChange({ ...form, defaultBusinessArea: e.target.value })}
              placeholder="z. B. Radsport-Events" />
            <datalist id="category-business-areas">
              {businessAreas.map(a => <option key={a} value={a} />)}
            </datalist>
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border"
            style={{ borderColor: 'var(--surface-border)', color: 'var(--text-muted)' }}>
            Abbrechen
          </button>
          <button onClick={onSave} disabled={busy}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  </ModalOverlay>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{hint}</span>}
  </label>
);

/**
 * Einen Umsatz auf mehrere Steuersätze aufteilen.
 *
 * Nicht jede Rechnung trägt einen einzigen Satz: eine Notarrechnung setzt steuerfreie
 * Auslagen hinter die Umsatzsteuer, eine Hotelrechnung mischt 7 % für die Übernachtung
 * mit 19 % fürs Frühstück. Was nicht aufgeteilt wird, gilt als steuerfrei — der Rest
 * muss also nicht auf null aufgehen, darf den Umsatz aber nicht übersteigen.
 */
const VatSplitDialog: React.FC<{
  transaction: BankTransaction;
  busy: boolean;
  onCancel: () => void;
  onSave: (splits: VatSplitRequest[]) => void;
}> = ({ transaction, busy, onCancel, onSave }) => {
  const gross = Math.abs(transaction.amountCents);

  const [rows, setRows] = useState<{ amount: string; rate: number; note: string }[]>(() =>
    transaction.vatSplits.length > 0
      ? transaction.vatSplits.map(v => ({
          amount: centsToInput(v.grossCents), rate: v.vatRatePercent, note: v.note ?? '',
        }))
      : [{ amount: centsToInput(gross), rate: transaction.vatRatePercent ?? 19, note: '' }],
  );

  const parsed = rows.map(r => ({ cents: inputToCents(r.amount), rate: r.rate, note: r.note }));
  const sum = parsed.reduce((s, r) => s + r.cents, 0);
  const rest = gross - sum;
  const vat = parsed.reduce((s, r) => s + Math.round((r.cents * r.rate) / (100 + r.rate)), 0);
  const tooMuch = sum > gross;

  const update = (i: number, patch: Partial<{ amount: string; rate: number; note: string }>) =>
    setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const field = 'px-2 py-1 rounded-lg text-sm border';
  const fieldStyle = {
    background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)',
  };

  return (
    <ModalOverlay ariaLabelledBy="split-dialog-title">
      <div className="min-h-full flex items-start justify-center p-4 py-10">
        <div className="w-full max-w-xl rounded-xl p-6"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <h2 id="split-dialog-title" className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
            Steuer aufteilen
          </h2>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {formatDate(transaction.bookingDate)} · {transaction.counterparty} ·{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{euro(gross)}</strong>
            {transaction.reference && <><br />{transaction.reference}</>}
          </p>

          <table className="w-full text-sm mb-3">
            <thead style={{ color: 'var(--text-muted)' }}>
              <tr className="text-left text-xs">
                <th className="py-1">Betrag brutto</th>
                <th className="py-1">Satz</th>
                <th className="py-1">Wofür</th>
                <th className="py-1 text-right">Vorsteuer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2">
                    <input className={`${field} w-24 nums-tabular`} style={fieldStyle} value={r.amount}
                      onChange={e => update(i, { amount: e.target.value })} />
                  </td>
                  <td className="py-1 pr-2">
                    <select className={field} style={fieldStyle} value={r.rate}
                      onChange={e => update(i, { rate: Number(e.target.value) })}>
                      {VAT_RATES.map(v => <option key={v} value={v}>{v} %</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <input className={`${field} w-full`} style={fieldStyle} value={r.note}
                      placeholder="optional" onChange={e => update(i, { note: e.target.value })} />
                  </td>
                  <td className="py-1 text-right nums-tabular" style={{ color: 'var(--text-muted)' }}>
                    {euro(Math.round((parsed[i].cents * r.rate) / (100 + r.rate)))}
                  </td>
                  <td className="py-1 pl-2 text-right">
                    {rows.length > 1 && (
                      <button onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}
                        className="text-xs underline" style={{ color: 'var(--text-muted)' }}>
                        weg
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            onClick={() => setRows(rs => [...rs, { amount: centsToInput(Math.max(0, rest)), rate: 0, note: '' }])}
            className="text-xs underline mb-4" style={{ color: 'var(--text-primary)' }}>
            + Zeile
          </button>

          <div className="rounded-lg p-3 text-sm nums-tabular mb-4"
            style={{ background: 'var(--page-bg)', border: '1px solid var(--surface-border)' }}>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-muted)' }}>aufgeteilt</span>
              <span style={{ color: 'var(--text-primary)' }}>{euro(sum)} von {euro(gross)}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-muted)' }}>
                {rest >= 0 ? 'Rest (gilt als steuerfrei)' : 'zu viel aufgeteilt'}
              </span>
              <span style={{ color: tooMuch ? 'var(--badge-red-text)' : 'var(--text-primary)' }}>
                {euro(Math.abs(rest))}
              </span>
            </div>
            <div className="flex justify-between font-bold mt-1 pt-1"
              style={{ borderTop: '1px solid var(--surface-border)' }}>
              <span style={{ color: 'var(--text-primary)' }}>Vorsteuer</span>
              <span style={{ color: 'var(--text-primary)' }}>{euro(vat)}</span>
            </div>
          </div>

          {tooMuch && (
            <p className="text-xs mb-3" style={{ color: 'var(--badge-red-text)' }}>
              Die Teilbeträge ergeben mehr als der Umsatz hergibt.
            </p>
          )}

          <div className="flex justify-between gap-2">
            {transaction.vatSplits.length > 0 ? (
              <button onClick={() => onSave([])} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-medium border disabled:opacity-50"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--badge-red-text)' }}>
                Aufteilung aufheben
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-muted)' }}>
                Abbrechen
              </button>
              <button
                disabled={busy || tooMuch || parsed.every(r => r.cents <= 0)}
                onClick={() => onSave(parsed
                  .filter(r => r.cents > 0)
                  .map((r, i) => ({
                    sortOrder: i, grossCents: r.cents, vatRatePercent: r.rate,
                    note: r.note.trim() || null,
                  })))}
                className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                {busy ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

/**
 * Zuordnung von Hand. Rechnungen mit passendem Bruttobetrag stehen oben — das ist
 * fast immer die gesuchte.
 */
const MatchDialog: React.FC<{
  transaction: BankTransaction;
  candidates: InvoiceMatchCandidate[];
  busy: boolean;
  onCancel: () => void;
  onAssign: (tx: BankTransaction, invoiceId: number, markPaid: boolean) => void;
}> = ({ transaction, candidates, busy, onCancel, onAssign }) => {
  const [markPaid, setMarkPaid] = useState(true);
  const [search, setSearch] = useState('');

  const sorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    return candidates
      .filter(c => !term || c.number.toLowerCase().includes(term) || c.clientName.toLowerCase().includes(term))
      .slice()
      .sort((a, b) => {
        const aFits = a.grossCents === transaction.amountCents ? 0 : 1;
        const bFits = b.grossCents === transaction.amountCents ? 0 : 1;
        if (aFits !== bFits) return aFits - bFits;
        const aOpen = a.status === 'PAID' ? 1 : 0;
        const bOpen = b.status === 'PAID' ? 1 : 0;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return b.invoiceDate.localeCompare(a.invoiceDate);
      });
  }, [candidates, search, transaction.amountCents]);

  return (
    <ModalOverlay ariaLabelledBy="match-dialog-title">
      <div className="min-h-full flex items-start justify-center p-4 py-10">
        <div className="w-full max-w-2xl rounded-xl p-6"
          style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>
          <h2 id="match-dialog-title" className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
            Zahlung zuordnen
          </h2>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {formatDate(transaction.bookingDate)} · {transaction.counterparty} ·{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{euro(transaction.amountCents)}</strong>
            {transaction.reference && <><br />{transaction.reference}</>}
          </p>

          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechnungsnummer oder Kunde"
            className="w-full px-3 py-2 rounded-lg text-sm border mb-3"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
          />

          <div className="max-h-80 overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--surface-border)' }}>
            {sorted.length === 0 ? (
              <p className="p-4 text-sm" style={{ color: 'var(--text-muted)' }}>Keine passende Rechnung gefunden.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {sorted.map(c => {
                    const fits = c.grossCents === transaction.amountCents;
                    return (
                      <tr key={c.id} className="border-t" style={{ borderColor: 'var(--surface-border)' }}>
                        <td className="px-3 py-2">
                          <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                            {c.number}
                            {c.status === 'PAID' && (
                              <span className="ml-2 text-[11px] font-normal" style={{ color: 'var(--text-muted)' }}>
                                bereits als bezahlt gebucht
                              </span>
                            )}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {c.clientName} · {formatDate(c.invoiceDate)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right nums-tabular whitespace-nowrap"
                          style={{ color: fits ? 'var(--badge-green-text)' : 'var(--text-muted)' }}>
                          {euro(c.grossCents)}
                          {fits && <div className="text-[10px]">Betrag passt</div>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button disabled={busy} onClick={() => onAssign(transaction, c.id, markPaid)}
                            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50">
                            zuordnen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} />
            Rechnung zugleich auf „bezahlt“ setzen
          </label>

          <div className="flex justify-end mt-4">
            <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border"
              style={{ borderColor: 'var(--surface-border)', color: 'var(--text-muted)' }}>
              Schließen
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default Banking;
